import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css } from '@emotion/css';
import { html, type TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';

/**
 * Floats next to the cursor during a row-reorder drag. Mounted under
 * `document.body` so `position: fixed` stays viewport-relative even
 * inside an Edgeless `transform`ed ancestor. Content (labels + count
 * badge) is set by the host on every pointermove.
 */
export class RowReorderGhost extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @state()
  accessor visible = false;

  @state()
  accessor clientX = 0;

  @state()
  accessor clientY = 0;

  @state()
  accessor labels: readonly string[] = [];

  show(opts: {
    clientX: number;
    clientY: number;
    labels: readonly string[];
  }): void {
    this.clientX = opts.clientX;
    this.clientY = opts.clientY;
    this.labels = opts.labels;
    this.visible = true;
  }

  hide(): void {
    this.visible = false;
  }

  override render(): TemplateResult {
    if (!this.visible || this.labels.length === 0) return html``;
    const MAX_VISIBLE = 8;
    const shown = this.labels.slice(0, MAX_VISIBLE);
    const overflow = this.labels.length - shown.length;
    return html`
      <div
        class=${ghostStyle}
        style="left:${this.clientX + 12}px; top:${this.clientY + 12}px"
      >
        ${shown.map(
          label =>
            html`<div class=${ghostRowStyle}>${label || 'Untitled'}</div>`
        )}
        ${overflow > 0
          ? html`<div class=${ghostOverflowStyle}>+${overflow} more</div>`
          : ''}
      </div>
    `;
  }
}

const ghostStyle = css({
  position: 'fixed',
  pointerEvents: 'none',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  padding: '4px 6px',
  borderRadius: '4px',
  minWidth: '120px',
  maxWidth: '280px',
  fontFamily: 'var(--streamlain-font-family)',
  fontSize: 'var(--streamlain-font-sm)',
  lineHeight: '1.4',
  boxShadow: 'var(--streamlain-shadow-2)',
  opacity: 0.92,

  '&&&': {
    background: 'var(--streamlain-v2-button-primary)',
    color: 'var(--streamlain-v2-button-pureWhiteText)',
  },

  '&&& *': {
    color: 'var(--streamlain-v2-button-pureWhiteText)',
  },
});

const ghostRowStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  padding: '2px 6px',
  borderRadius: '3px',
  fontWeight: 500,
  background: 'rgba(255, 255, 255, 0.14)',
});

const ghostOverflowStyle = css({
  padding: '1px 6px',
  fontSize: '11px',
  fontWeight: 600,
  opacity: 0.85,
});

declare global {
  interface HTMLElementTagNameMap {
    'dv-row-reorder-ghost': RowReorderGhost;
  }
}
